import React, { useState, useRef, useEffect } from "react";
import "./dropdown.css"; 
import { down_Arr, roundCancelIcon } from "../../../assets";
import colors from "../../../utils/colors";
import { Flex, Img, Text } from "@chakra-ui/react";

interface DropdownProps {
  options: string[];
  onSelect: (selectedOption: any) => void;
  className?: string;
  prefixText?: string;
  valueFont?: string;
  placeholder?: string;
  inputStyles?: string;
  isMulti?: boolean;
  max?: string;
  value?: string; 
  multiValue?: string[];
  triggerOpen?: boolean;
  onDropdownClose?: () => void;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  onSelect,
  className,
  prefixText = "",
  valueFont,
  placeholder = "Select",
  inputStyles,
  isMulti = false,
  max,
  value, 
  multiValue,
  triggerOpen,
  onDropdownClose,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOpions, setSelectedOptions] = useState<string[]>(
    multiValue ?? []
  );
  const dropdownRef: any = useRef(null);

  const isChipViewForSingleSelect =
    !isMulti && value && value !== placeholder && value !== "All";

  const handleOptionSelect = (option: string) => {
    if (!isMulti) {
      onSelect(String(option));
      setIsOpen(false);
      if (onDropdownClose) {
        onDropdownClose();
      }
    } else {
      let newSelectedOptions: string[];
      if (selectedOpions?.includes(option)) {
        newSelectedOptions = selectedOpions?.filter((item) => item !== option);
      } else {
        if (selectedOpions?.length < (parseInt(max || "999"))) {
          newSelectedOptions = [...selectedOpions, option];
        } else {
          newSelectedOptions = [...selectedOpions]; 
        }
      }
      setSelectedOptions(newSelectedOptions);
      onSelect(newSelectedOptions);
    }
  };

  const handleClearSingleChip = (e: React.MouseEvent) => {
    e.stopPropagation(); 
    onSelect(placeholder); 
  };

  const handleRemoveMultiChip = (option: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const new_ = selectedOpions?.filter((item) => item !== option);
    setSelectedOptions(new_);
    onSelect(new_);
  };

  const toggleDropdown = () => {
    if (isMulti || !isChipViewForSingleSelect) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      if (!newIsOpen && onDropdownClose) {
        onDropdownClose();
      }
    }
  };

  const handleClickOutside = (event: MouseEvent) => { 
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) { 
      if (isOpen) { 
        setIsOpen(false);
        if (onDropdownClose) {
          onDropdownClose();
        }
      }
    }
  };

  useEffect(() => {
    if (triggerOpen && !isOpen) {
      setIsOpen(true);
    }
  }, [triggerOpen, isOpen]);

  useEffect(() => {
    if (isMulti && multiValue) {
        setSelectedOptions(multiValue);
    }
  }, [isMulti, multiValue]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside); 
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onDropdownClose]); 

  return (
    <div
      className={`custom-dropdown relative flex min-w-[120px] max-h-[40px]`} 
      ref={dropdownRef}
    >
      <div
        className={`dropdown-header flex justify-between items-center ${className} pl-2 text-xs rounded-md !border-[#e8e1e1] ${
          isOpen ? "open" : ""
        } ${isChipViewForSingleSelect && !isMulti ? 'cursor-default' : 'cursor-pointer'}`}
        onClick={toggleDropdown}
      >
        <Flex
          alignItems="center"
          style={{ fontWeight: valueFont }}
          gap={{ base: "5px", sm: "10px" }} 
          className={`text-sm ${inputStyles} flex-grow`}
          flexWrap={"wrap"}
        >
          {prefixText && (
            <span
              className="text-xs whitespace-nowrap"
              style={{ color: colors.placeholderColor }}
            >
              {prefixText}
            </span>
          )}

          {isMulti ? (
            selectedOpions?.length > 0 ? (
              selectedOpions?.map((option, index) => (
                <Flex
                  key={index}
                  backgroundColor={colors.lightGreen}
                  p={"6px 10px"}
                  alignItems={"center"}
                  gap={"10px"}
                  borderRadius={"16px"}
                  color={colors.dark}
                >
                  <Text mt={"-2px"} fontSize="xs">{option}</Text>
                  <Img
                    src={roundCancelIcon}
                    h={"14px"}
                    cursor="pointer"
                    onClick={(e) => handleRemoveMultiChip(option, e)}
                  />
                </Flex>
              ))
            ) : (
              <span style={{ color: colors.placeholderColor }} className="text-xs">
                {placeholder}
              </span>
            )
          ) : isChipViewForSingleSelect ? (
            <Flex
              backgroundColor={colors.lightGreen} 
              p={"6px 10px"}
              alignItems={"center"}
              gap={"10px"}
              borderRadius={"16px"}
              color={colors.dark}
              fontSize="xs"
            >
              <Text mt={"-2px"}>{value}</Text> 
              <Img
                src={roundCancelIcon}
                h={"14px"}
                cursor="pointer"
                onClick={handleClearSingleChip}
              />
            </Flex>
          ) : (
            <span
              style={{ color: colors.placeholderColor, whiteSpace: "nowrap" }}
              className={`text-xs ${inputStyles}`}
            >
              {value || placeholder} 
            </span>
          )}
        </Flex>

        {(!isChipViewForSingleSelect || isMulti) && (
          <img src={down_Arr} className="h-[6px] mr-[10px] ml-1 sm:ml-2" alt="toggle dropdown"/> 
        )}
      </div>

      {isOpen && (
        <div className="dropdown-options w-full pt-[13px] !max-h-[400px]"> 
          {isMulti && max && ( 
            <Text fontWeight={700} fontSize={"14px"} px={"20px"} pb={"13px"}>
              Select a maximum of {max}
            </Text>
          )}
          {options.map((option, index) => (
            <div
              key={index}
              className={`dropdown-option ${
                (isMulti ? selectedOpions?.includes(option) : value === option && value !== placeholder) && `!text-[#00B680]`
              }`} 
              onClick={() => handleOptionSelect(option)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
